import React, { useEffect, useState } from 'react';
import { X, Star, TrendingUp, AlertTriangle, CheckCircle, Info, Loader2, ArrowUpDown, DollarSign, ChevronDown, ChevronUp, Target, Sparkles, Printer } from 'lucide-react';
import { getDoc, doc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { supabase } from '../supabase';
import {
  calculatePropertyScore,
  type PropertyForScoring,
  type NeighborhoodScoreRow,
  type NeighborhoodStatsRow,
  type PropertyScoreResult,
  type QualityBreakdownItem,
} from '../utils/propertyScoreEngine';
import PropertyRatingPrintReport from './PropertyRatingPrintReport';
import { displayCity, displayNeighborhood, displayPropertyType, displayCondition } from '../utils/displayLabels';

interface ComparableTransaction {
  full_address: string | null;
  size_sqm: number | null;
  rooms: number | null;
  floor: string | null;
  price: number | null;
  price_per_sqm: number | null;
  transaction_date: string | null;
}

interface PricingAnalysis {
  askingPrice: number;
  size: number;
  pricePerSqm: number;
  benchmarkPerSqm: number;
  benchmarkLabel: string;
  gapPercent: number;
  status: string;
  statusColor: string;
  statusBg: string;
}

interface Props {
  formId: string;
  city: string;
  neighborhood: string;
  onClose: () => void;
}

interface AdvisorResult {
  executive_summary: string;
  recommended_marketing_strategy: string;
  key_selling_points: string[];
  risk_factors: string[];
  marketability_assessment: {
    level: string;
    explanation: string;
  };
  pricing_recommendation?: string;
  action_items?: string[];
}

const LABEL_COLOR: Record<string, string> = {
  'מצוין': 'text-green-400',
  'טוב': 'text-blue-400',
  'בינוני': 'text-yellow-400',
  'חלש': 'text-red-400',
};

const LABEL_BG: Record<string, string> = {
  'מצוין': 'bg-green-500/20 border-green-500/40',
  'טוב': 'bg-blue-500/20 border-blue-500/40',
  'בינוני': 'bg-yellow-500/20 border-yellow-500/40',
  'חלש': 'bg-red-500/20 border-red-500/40',
};

function buildPricingAnalysis(
  askingPrice: number,
  size: number,
  stats: NeighborhoodStatsRow | null,
): PricingAnalysis | null {
  if (!askingPrice || !size) return null;

  const pricePerSqm = askingPrice / size;
  const benchmarkPerSqm = stats?.median_price_per_sqm_transactions ?? stats?.avg_price_per_sqm ?? null;
  if (!benchmarkPerSqm) return null;

  const benchmarkLabel = stats?.median_price_per_sqm_transactions != null
    ? 'חציון עסקאות בפועל'
    : 'ממוצע לפי מקורות נדל"ן';

  const gapPercent = ((pricePerSqm - benchmarkPerSqm) / benchmarkPerSqm) * 100;
  const gap = Math.round(gapPercent * 10) / 10;

  let status: string;
  let statusColor: string;
  let statusBg: string;

  if (gap <= -10) {
    status = 'הזדמנות – מתחת למחיר השוק';
    statusColor = 'text-green-400';
    statusBg = 'bg-green-500/15 border-green-500/30';
  } else if (gap <= -3) {
    status = 'מתומחר אטרקטיבית';
    statusColor = 'text-green-400';
    statusBg = 'bg-green-500/15 border-green-500/30';
  } else if (gap < 3) {
    status = 'מתומחר נכון';
    statusColor = 'text-green-400';
    statusBg = 'bg-green-500/15 border-green-500/30';
  } else if (gap < 10) {
    status = 'מעט מעל מחיר השוק';
    statusColor = 'text-yellow-400';
    statusBg = 'bg-yellow-500/15 border-yellow-500/30';
  } else if (gap < 20) {
    status = 'יקר ביחס לשוק';
    statusColor = 'text-orange-400';
    statusBg = 'bg-orange-500/15 border-orange-500/30';
  } else {
    status = 'יקר משמעותית ביחס לשוק';
    statusColor = 'text-red-400';
    statusBg = 'bg-red-500/15 border-red-500/30';
  }

  return { askingPrice, size, pricePerSqm, benchmarkPerSqm, benchmarkLabel, gapPercent: gap, status, statusColor, statusBg };
}

interface RvRecommendedPrice {
  baseMarketPrice: number;
  qualityScore: number;
  qualityAdjustmentPercent: number;
  recommendedPrice: number;
  recommendedGapPercent: number;
  status: string;
  statusColor: string;
  statusBg: string;
}

function buildRvRecommendedPrice(
  askingPrice: number,
  size: number,
  stats: NeighborhoodStatsRow | null,
  qualityScore: number,
): RvRecommendedPrice | null {
  if (!size) return null;
  const benchmarkPerSqm = stats?.median_price_per_sqm_transactions ?? stats?.avg_price_per_sqm ?? null;
  if (!benchmarkPerSqm) return null;

  const baseMarketPrice = benchmarkPerSqm * size;

  let qualityAdjustmentPercent: number;
  if (qualityScore >= 90) qualityAdjustmentPercent = 8;
  else if (qualityScore >= 80) qualityAdjustmentPercent = 5;
  else if (qualityScore >= 70) qualityAdjustmentPercent = 2;
  else if (qualityScore >= 60) qualityAdjustmentPercent = 0;
  else if (qualityScore >= 50) qualityAdjustmentPercent = -3;
  else qualityAdjustmentPercent = -6;

  const rawRecommended = baseMarketPrice * (1 + qualityAdjustmentPercent / 100);
  const recommendedPrice = Math.round(rawRecommended / 10000) * 10000;

  const recommendedGapPercent = askingPrice && recommendedPrice
    ? Math.round(((askingPrice - recommendedPrice) / recommendedPrice) * 1000) / 10
    : 0;

  let status: string;
  let statusColor: string;
  let statusBg: string;

  if (recommendedGapPercent <= -5) {
    status = 'המחיר המבוקש נמוך מהמחיר המומלץ';
    statusColor = 'text-green-400';
    statusBg = 'bg-green-500/15 border-green-500/30';
  } else if (recommendedGapPercent < 5) {
    status = 'המחיר המבוקש תואם למחיר המומלץ';
    statusColor = 'text-green-400';
    statusBg = 'bg-green-500/15 border-green-500/30';
  } else if (recommendedGapPercent < 10) {
    status = 'המחיר המבוקש מעט מעל המחיר המומלץ';
    statusColor = 'text-yellow-400';
    statusBg = 'bg-yellow-500/15 border-yellow-500/30';
  } else if (recommendedGapPercent < 20) {
    status = 'המחיר המבוקש גבוה מהמחיר המומלץ';
    statusColor = 'text-orange-400';
    statusBg = 'bg-orange-500/15 border-orange-500/30';
  } else {
    status = 'המחיר המבוקש גבוה משמעותית מהמחיר המומלץ';
    statusColor = 'text-red-400';
    statusBg = 'bg-red-500/15 border-red-500/30';
  }

  return { baseMarketPrice, qualityScore, qualityAdjustmentPercent, recommendedPrice, recommendedGapPercent, status, statusColor, statusBg };
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function StatRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex items-baseline justify-between gap-2 text-xs">
      <span className="text-gray-400 leading-snug">{label}</span>
      <span className={`font-medium shrink-0 ${value ? 'text-green-300' : 'text-gray-600'}`}>
        {value ?? 'מידע יתווסף בהמשך'}
      </span>
    </div>
  );
}

function PricingRow({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-2 text-xs">
      <span className="text-gray-400 leading-snug">{label}</span>
      <span className={`font-medium shrink-0 ${muted ? 'text-gray-400' : 'text-white'}`}>{value}</span>
    </div>
  );
}

function ScoreBar({ score, max }: { score: number; max: number }) {
  const pct = Math.round((score / max) * 100);
  const color = pct >= 75 ? 'bg-green-500' : pct >= 50 ? 'bg-blue-500' : pct >= 35 ? 'bg-yellow-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-700 rounded-full h-1.5 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-300 w-10 text-left shrink-0">{score}/{max}</span>
    </div>
  );
}

function QualityBar({ score, max }: { score: number; max: number }) {
  const pct = Math.round((score / max) * 100);
  const color = pct >= 75 ? 'bg-green-500/70' : pct >= 50 ? 'bg-blue-500/70' : pct >= 35 ? 'bg-yellow-500/70' : 'bg-red-500/70';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-800 rounded-full h-1 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-400 w-8 text-left shrink-0 tabular-nums">{Math.round(score)}/{max}</span>
    </div>
  );
}

function QualityBreakdownRow({ item }: { item: QualityBreakdownItem }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-400">{item.parameter}</span>
      </div>
      <QualityBar score={item.score} max={item.maxScore} />
    </div>
  );
}

// ── Neighborhood text helpers ──────────────────────────────────────────────────
function cleanNeighborhoodText(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw
    .replace(/^"+|"+$/g, '')   // strip wrapping quotes
    .replace(/\\"/g, '"')       // unescape \"
    .trim();
}

function parseNeighborhoodItems(raw: string | null | undefined): string[] {
  const cleaned = cleanNeighborhoodText(raw);
  if (!cleaned) return [];
  return cleaned
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
}

const PREVIEW_CHARS = 100;

function NeighborhoodPanel({
  variant,
  raw,
  onExpand,
}: {
  variant: 'strengths' | 'weaknesses';
  raw: string | null | undefined;
  onExpand: () => void;
}) {
  const isStrengths = variant === 'strengths';
  const items = parseNeighborhoodItems(raw);

  const containerCls = isStrengths
    ? 'bg-green-900/20 border border-green-700/30 rounded-lg p-3'
    : 'bg-red-900/20 border border-red-700/30 rounded-lg p-3';
  const titleCls = isStrengths ? 'text-green-400' : 'text-red-400';
  const itemCls = isStrengths ? 'text-green-300' : 'text-red-300';
  const fallbackCls = isStrengths ? 'text-green-600' : 'text-red-600';
  const title = isStrengths ? 'חוזקות' : 'חולשות';
  const fallback = isStrengths ? 'לא נמצאו חוזקות זמינות' : 'אין חולשות משמעותיות';
  const Icon = isStrengths ? CheckCircle : AlertTriangle;

  const fullText = cleanNeighborhoodText(raw);
  const needsExpand = fullText.length > PREVIEW_CHARS;

  // Build a short preview: first item(s) until we hit ~PREVIEW_CHARS characters
  let previewItems: string[] = [];
  let charCount = 0;
  for (const item of items) {
    if (charCount + item.length > PREVIEW_CHARS && previewItems.length >= 1) break;
    previewItems.push(item);
    charCount += item.length;
  }

  return (
    <div className={containerCls}>
      <div className="flex items-center gap-1.5 mb-2">
        <Icon size={13} className={titleCls} />
        <span className={`text-xs font-bold ${titleCls}`}>{title}</span>
      </div>
      {items.length === 0 ? (
        <p className={`text-xs ${fallbackCls}`}>{fallback}</p>
      ) : (
        <>
          <ul className="space-y-1">
            {previewItems.map((s, i) => (
              <li key={i} className={`text-xs ${itemCls} leading-snug`}>• {s}</li>
            ))}
          </ul>
          {needsExpand && (
            <button
              type="button"
              onClick={onExpand}
              className={`mt-2 text-xs underline underline-offset-2 ${titleCls} hover:opacity-80 transition-opacity`}
            >
              לצפייה מלאה
            </button>
          )}
        </>
      )}
    </div>
  );
}

function NeighborhoodExpandModal({
  variant,
  raw,
  onClose,
}: {
  variant: 'strengths' | 'weaknesses';
  raw: string | null | undefined;
  onClose: () => void;
}) {
  const isStrengths = variant === 'strengths';
  const items = parseNeighborhoodItems(raw);
  const title = isStrengths ? 'חוזקות השכונה' : 'חולשות השכונה';
  const itemCls = isStrengths ? 'text-green-300' : 'text-red-300';
  const headerCls = isStrengths ? 'text-green-400' : 'text-red-400';
  const Icon = isStrengths ? CheckCircle : AlertTriangle;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4"
      dir="rtl"
      onClick={onClose}
    >
      <div
        className="bg-gray-800 rounded-xl border border-gray-600 shadow-2xl w-full max-w-md max-h-[70vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 shrink-0">
          <div className="flex items-center gap-2">
            <Icon size={14} className={headerCls} />
            <span className={`text-sm font-bold ${headerCls}`}>{title}</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X size={16} />
          </button>
        </div>
        <div className="overflow-y-auto px-4 py-3">
          {items.length === 0 ? (
            <p className="text-xs text-gray-500">אין מידע זמין</p>
          ) : (
            <ul className="space-y-2">
              {items.map((s, i) => (
                <li key={i} className={`text-sm ${itemCls} leading-relaxed`}>• {s}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PropertyScoreModal({ formId, city, neighborhood, onClose }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PropertyScoreResult | null>(null);
  const [nsRow, setNsRow] = useState<NeighborhoodScoreRow | null>(null);
  const [statsRow, setStatsRow] = useState<NeighborhoodStatsRow | null>(null);
  const [comparables, setComparables] = useState<ComparableTransaction[]>([]);
  const [pricing, setPricing] = useState<PricingAnalysis | null | 'no-size'>(null);
  const [rvRecommended, setRvRecommended] = useState<RvRecommendedPrice | null | 'no-data'>(null);
  const [propertyAddress, setPropertyAddress] = useState<string | null>(null);
  const [qualityExpanded, setQualityExpanded] = useState(false);
  const [expandPanel, setExpandPanel] = useState<'strengths' | 'weaknesses' | null>(null);
  const [advisor, setAdvisor] = useState<AdvisorResult | null>(null);
  const [advisorLoading, setAdvisorLoading] = useState(false);
  const [advisorError, setAdvisorError] = useState<string | null>(null);
  const [showPrintReport, setShowPrintReport] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const trimCity = city.trim();
        const trimNeigh = (neighborhood || '').trim();

        const [firestoreSnap, scoresRes, statsRes, comparablesRes] = await Promise.all([
          getDoc(doc(db, 'forms', formId)),
          supabase
            .from('market_neighborhood_scores')
            .select('location_score, confidence, location_level, reason, strengths, weaknesses')
            .eq('city', trimCity)
            .eq('neighborhood_name', trimNeigh)
            .maybeSingle(),
          supabase
            .from('market_neighborhood_stats')
            .select('avg_price_per_sqm, transactions_count, data_period, avg_price_per_sqm_transactions, median_price_per_sqm_transactions, transactions_count_12m, transactions_stats_period, active_listings_count, active_listings_updated_at')
            .eq('city', trimCity)
            .eq('neighborhood_name', trimNeigh)
            .maybeSingle(),
          supabase
            .from('market_comparable_transactions')
            .select('full_address, size_sqm, rooms, floor, price, price_per_sqm, transaction_date')
            .eq('city', trimCity)
            .eq('neighborhood_name', trimNeigh)
            .order('transaction_date', { ascending: false })
            .limit(5),
        ]);

        if (cancelled) return;

        if (!firestoreSnap.exists()) {
          setError('לא נמצאו נתוני נכס');
          return;
        }

        const data = firestoreSnap.data();
        const p = data.property || {};

        const addrStreet = [data.address?.street, data.address?.houseNumber].filter(Boolean).join(' ');
        setPropertyAddress(addrStreet || null);

        const property: PropertyForScoring = {
          propertyType: p.propertyType || data.propertyType,
          propertyAge: p.propertyAge || data.propertyAge,
          propertyCondition: p.propertyCondition || data.propertyCondition,
          size: p.size || data.size,
          rooms: p.rooms || data.rooms,
          bathrooms: p.bathrooms || data.bathrooms,
          floor: p.floor ?? data.floor,
          parkingSpots: p.parkingSpots ?? data.parkingSpots,
          hasElevator: p.hasElevator ?? data.hasElevator,
          hasSaferoom: p.hasSaferoom ?? data.hasSaferoom,
          hasBalcony: p.hasBalcony ?? data.hasBalcony,
          balconySize: p.balconySize ?? data.balconySize,
          view: p.view || data.view,
          airDirection: p.airDirection || data.airDirection,
          airDirectionLabel: p.airDirectionLabel || data.airDirectionLabel,
          nearbyPlaces: data.nearbyPlaces,
        };

        const ns: NeighborhoodScoreRow | null = scoresRes.data ?? null;
        const st: NeighborhoodStatsRow | null = statsRes.data ?? null;

        console.log('[Market Stats]', st);

        setNsRow(ns);
        setStatsRow(st);
        setComparables(comparablesRes.data ?? []);
        setResult(calculatePropertyScore(property, ns, st));

        const askingPrice = Number(p.price || data.price || 0);
        const size = Number(p.size || data.size || 0);
        if (!size) {
          setPricing('no-size');
          setRvRecommended('no-data');
        } else {
          setPricing(buildPricingAnalysis(askingPrice, size, st));
          const scoreResult = calculatePropertyScore(property, ns, st);
          const rv = buildRvRecommendedPrice(askingPrice, size, st, scoreResult.qualityResult.qualityScore);
          setRvRecommended(rv ?? 'no-data');
        }
      } catch (e) {
        if (!cancelled) setError('שגיאה בטעינת הדירוג');
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [formId, city, neighborhood]);

  // Fetch AI advisor once main data is ready
  useEffect(() => {
    if (!result || loading) return;
    let cancelled = false;

    async function fetchAdvisor() {
      setAdvisorLoading(true);
      setAdvisorError(null);
      try {
        const user = auth.currentUser;
        if (!user) {
          console.warn('[AI Advisor] No auth.currentUser — skipping');
          return;
        }
        const idToken = await user.getIdToken();
        console.log('[AI Advisor] Got idToken, uid:', user.uid, 'token length:', idToken.length);

        const pricingData = pricing && pricing !== 'no-size' ? pricing : null;
        const rvData = rvRecommended && rvRecommended !== 'no-data' ? rvRecommended : null;

        const payload = {
          property_id: formId,
          city: displayCity(city),
          neighborhood: displayNeighborhood(neighborhood),
          propertyType: displayPropertyType(result.breakdown[0]?.parameter),
          overallScore: result.totalScore,
          qualityScore: result.qualityResult.qualityScore,
          locationScore: nsRow?.location_score ?? null,
          median_price_per_sqm: statsRow?.median_price_per_sqm_transactions ?? null,
          avg_price_per_sqm: statsRow?.avg_price_per_sqm_transactions ?? null,
          transactions_count_12m: statsRow?.transactions_count_12m ?? null,
          property_price_per_sqm: pricingData?.pricePerSqm ?? null,
          reference_price_per_sqm: pricingData?.benchmarkPerSqm ?? null,
          priceGapPercent: pricingData?.gapPercent ?? null,
          recommendedPrice: rvData?.recommendedPrice ?? null,
          qualityAdjustmentPercent: rvData?.qualityAdjustmentPercent ?? null,
          askingPrice: pricingData?.askingPrice ?? null,
          strengths: nsRow?.strengths ?? null,
          weaknesses: nsRow?.weaknesses ?? null,
        };

        console.log('[AI Advisor] Payload:', JSON.stringify(payload, null, 2));

        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const res = await fetch(`${supabaseUrl}/functions/v1/generate-property-advisor`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
          },
          body: JSON.stringify(payload),
        });

        console.log('[AI Advisor] HTTP status:', res.status);
        const rawText = await res.text();
        console.log('[AI Advisor] Response body:', rawText);

        if (!res.ok) {
          console.error('[AI Advisor] Non-OK response:', res.status, rawText);
          if (!cancelled) setAdvisorError(`שגיאה ${res.status}`);
          return;
        }

        let data: { advice?: AdvisorResult };
        try {
          data = JSON.parse(rawText);
        } catch (parseErr) {
          console.error('[AI Advisor] JSON parse error:', parseErr, 'raw:', rawText);
          if (!cancelled) setAdvisorError('שגיאה בפענוח תשובת AI');
          return;
        }

        if (!cancelled && data.advice) setAdvisor(data.advice);
        else if (!cancelled) {
          console.error('[AI Advisor] Missing advice field in response:', data);
          setAdvisorError('לא ניתן לטעון המלצות AI');
        }
      } catch (e) {
        console.error('[AI Advisor] Caught exception:', e);
        if (!cancelled) setAdvisorError('שגיאה בטעינת יועץ AI');
      } finally {
        if (!cancelled) setAdvisorLoading(false);
      }
    }

    fetchAdvisor();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, loading]);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4" dir="rtl">
      <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col border border-gray-700">
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-700 shrink-0">
          <div className="flex items-start gap-2">
            <Star size={16} className="text-yellow-400 mt-0.5 shrink-0" />
            <div>
              <span className="text-white font-bold text-sm">פירוט דירוג הנכס</span>
              {propertyAddress && (
                <p className="text-xs font-semibold text-gray-200 mt-0.5 leading-snug">{propertyAddress}</p>
              )}
              {(neighborhood || city) && (
                <p className="text-xs text-gray-400 mt-0.5 leading-snug">
                  {[neighborhood, city].filter(Boolean).join(' | ')}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPrintReport(true)}
              title="הדפס דו״ח"
              className="text-gray-400 hover:text-blue-400 transition-colors mt-0.5"
            >
              <Printer size={18} />
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors mt-0.5">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-5">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
              <Loader2 size={28} className="animate-spin" />
              <span className="text-sm">מחשב דירוג נכס...</span>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-red-400 text-sm py-8 justify-center">
              <AlertTriangle size={16} />
              <span>{error}</span>
            </div>
          )}

          {result && !loading && (
            <>
              {/* Total score */}
              <div className={`rounded-xl border p-5 text-center ${LABEL_BG[result.scoreLabel]}`}>
                <div className={`text-5xl font-black ${LABEL_COLOR[result.scoreLabel]}`}>{result.totalScore}</div>
                <div className="text-gray-400 text-xs mt-0.5">מתוך 100</div>
                <div className={`mt-2 text-sm font-bold ${LABEL_COLOR[result.scoreLabel]}`}>{result.scoreLabel}</div>
              </div>

              {/* 3-component breakdown */}
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">פירוט ציונים</h3>
                <div className="space-y-2">
                  {result.breakdown.map(item => {
                    const isQuality = item.parameter === 'איכות הנכס';
                    return (
                      <div key={item.parameter} className="bg-gray-700/50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-semibold text-gray-200">{item.parameter}</span>
                          <span className="text-xs text-gray-400">{item.score}/{item.maxScore}</span>
                        </div>
                        <ScoreBar score={item.score} max={item.maxScore} />
                        {isQuality ? (
                          <>
                            <p className="text-xs text-gray-500 mt-1.5 leading-snug">{item.description}</p>
                            {/* Expandable quality sub-breakdown */}
                            <button
                              type="button"
                              onClick={() => setQualityExpanded(v => !v)}
                              className="mt-2 flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                            >
                              {qualityExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                              ציון איכות נכס: {result.qualityResult.qualityScore}/100
                              <span className="text-gray-500 mr-1">({result.qualityResult.qualityLabel})</span>
                            </button>
                            {qualityExpanded && (
                              <div className="mt-2 pt-2 border-t border-gray-600/50 space-y-2">
                                {result.qualityResult.breakdown.map(qi => (
                                  <QualityBreakdownRow key={qi.parameter} item={qi} />
                                ))}
                              </div>
                            )}
                          </>
                        ) : (
                          <p className="text-xs text-gray-500 mt-1.5 leading-snug">{item.description}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Strengths & weaknesses */}
              <div className="grid grid-cols-2 gap-3">
                <NeighborhoodPanel
                  variant="strengths"
                  raw={nsRow?.strengths as string | null | undefined}
                  onExpand={() => setExpandPanel('strengths')}
                />
                <NeighborhoodPanel
                  variant="weaknesses"
                  raw={nsRow?.weaknesses as string | null | undefined}
                  onExpand={() => setExpandPanel('weaknesses')}
                />
              </div>

              {/* Neighborhood GPT details */}
              {nsRow && (
                <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-3 space-y-1.5">
                  <div className="flex items-center gap-1.5 mb-1">
                    <TrendingUp size={13} className="text-blue-400" />
                    <span className="text-xs font-bold text-blue-400">נתוני שכונה (GPT)</span>
                  </div>
                  {nsRow.location_level && (
                    <div className="text-xs text-gray-300">רמת מיקום: <span className="text-white font-medium">{nsRow.location_level}</span></div>
                  )}
                  <div className="text-xs text-gray-300">ציון מיקום: <span className="text-white font-medium">{nsRow.location_score}/23</span>
                    {nsRow.confidence && <span className="text-gray-500 mr-1">({nsRow.confidence})</span>}
                  </div>
                  {nsRow.reason && (
                    <div className="text-xs text-gray-400 italic leading-snug">{nsRow.reason}</div>
                  )}
                </div>
              )}

              {/* Market stats */}
              <div className="bg-gray-700/40 border border-gray-600/40 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-1.5 mb-1">
                  <Info size={13} className="text-gray-400" />
                  <span className="text-xs font-bold text-gray-400">נתוני שוק לפי שכונה</span>
                </div>
                <StatRow
                  label='מחיר ממוצע למ"ר לפי מקורות נדל"ן'
                  value={statsRow?.avg_price_per_sqm != null ? `₪${Math.round(statsRow.avg_price_per_sqm).toLocaleString('he-IL')}` : null}
                />
                <StatRow
                  label='חציון עסקאות בפועל'
                  value={statsRow?.median_price_per_sqm_transactions != null ? `₪${Math.round(statsRow.median_price_per_sqm_transactions).toLocaleString('he-IL')}` : null}
                />
                <StatRow
                  label='ממוצע עסקאות בפועל'
                  value={statsRow?.avg_price_per_sqm_transactions != null ? `₪${Math.round(statsRow.avg_price_per_sqm_transactions).toLocaleString('he-IL')}` : null}
                />
                <StatRow
                  label="מספר עסקאות בחישוב"
                  value={statsRow?.transactions_count_12m != null ? String(statsRow.transactions_count_12m) : null}
                />
                <StatRow
                  label="תקופת נתונים"
                  value={statsRow?.transactions_stats_period || statsRow?.data_period || null}
                />
                {statsRow?.active_listings_count !== null && statsRow?.active_listings_count !== undefined && (() => {
                  const count = statsRow.active_listings_count as number;
                  const level =
                    count <= 50 ? 'היצע נמוך' :
                    count <= 150 ? 'היצע בינוני' :
                    count <= 400 ? 'היצע גבוה' :
                    'היצע גבוה מאוד';
                  const updatedAt = statsRow.active_listings_updated_at;
                  return (
                    <>
                      <div className="border-t border-gray-600/40 pt-2 mt-1" />
                      <StatRow
                        label="היצע נכסים פעיל בשכונה"
                        value={`${count.toLocaleString('he-IL')} מודעות`}
                      />
                      <StatRow label="רמת היצע" value={level} />
                      {updatedAt !== null && updatedAt !== undefined && (
                        <StatRow
                          label="עודכן לאחרונה"
                          value={formatDate(updatedAt.slice(0, 10))}
                        />
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Property pricing analysis */}
              <div className={`border rounded-lg p-3 space-y-2.5 ${pricing && pricing !== 'no-size' ? pricing.statusBg : 'bg-gray-700/30 border-gray-600/40'}`}>
                <div className="flex items-center gap-1.5">
                  <DollarSign size={13} className="text-gray-300" />
                  <span className="text-xs font-bold text-gray-300">הערכת תמחור נכס</span>
                </div>

                {pricing === 'no-size' && (
                  <p className="text-xs text-gray-500">לא ניתן לחשב מחיר למ״ר – גודל הנכס חסר</p>
                )}

                {pricing === null && (
                  <p className="text-xs text-gray-500">אין נתוני שוק זמינים להשוואה</p>
                )}

                {pricing && pricing !== 'no-size' && (
                  <>
                    <div className="space-y-1.5">
                      <PricingRow label="מחיר מבוקש" value={`₪${Math.round(pricing.askingPrice).toLocaleString('he-IL')}`} />
                      <PricingRow label='מחיר למ"ר' value={`₪${Math.round(pricing.pricePerSqm).toLocaleString('he-IL')}`} />
                      <PricingRow label={`מחיר ייחוס (${pricing.benchmarkLabel})`} value={`₪${Math.round(pricing.benchmarkPerSqm).toLocaleString('he-IL')}`} muted />
                    </div>
                    <div className="flex items-center justify-between pt-1.5 border-t border-gray-600/40">
                      <span className="text-xs text-gray-400">פער מהשוק</span>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold tabular-nums ${pricing.statusColor}`}>
                          {pricing.gapPercent > 0 ? '+' : ''}{pricing.gapPercent.toFixed(1)}%
                        </span>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${pricing.statusBg} ${pricing.statusColor}`}>
                          {pricing.status}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* RV Recommended Price */}
              <div className={`border rounded-lg p-3 space-y-2.5 ${rvRecommended && rvRecommended !== 'no-data' ? rvRecommended.statusBg : 'bg-gray-700/30 border-gray-600/40'}`}>
                <div className="flex items-center gap-1.5">
                  <Target size={13} className="text-blue-300" />
                  <span className="text-xs font-bold text-blue-300">מחיר מומלץ RV</span>
                </div>

                {rvRecommended === 'no-data' && (
                  <p className="text-xs text-gray-500">לא ניתן לחשב מחיר מומלץ – חסרים נתוני שוק או גודל נכס</p>
                )}

                {rvRecommended && rvRecommended !== 'no-data' && (
                  <>
                    <div className="space-y-1.5">
                      <PricingRow
                        label='מחיר בסיס לפי השוק'
                        value={`₪${Math.round(rvRecommended.baseMarketPrice).toLocaleString('he-IL')}`}
                        muted
                      />
                      <PricingRow
                        label='ציון איכות הנכס'
                        value={`${rvRecommended.qualityScore}/100`}
                        muted
                      />
                      <PricingRow
                        label='התאמת איכות'
                        value={`${rvRecommended.qualityAdjustmentPercent > 0 ? '+' : ''}${rvRecommended.qualityAdjustmentPercent}%`}
                        muted
                      />
                      <div className="flex items-baseline justify-between gap-2 text-xs pt-1 border-t border-gray-600/40">
                        <span className="text-gray-200 font-semibold leading-snug">מחיר מומלץ RV</span>
                        <span className="font-bold text-white shrink-0 text-sm">
                          ₪{rvRecommended.recommendedPrice.toLocaleString('he-IL')}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-1.5 border-t border-gray-600/40">
                      <span className="text-xs text-gray-400">פער מול מחיר מבוקש</span>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold tabular-nums ${rvRecommended.statusColor}`}>
                          {rvRecommended.recommendedGapPercent > 0 ? '+' : ''}{rvRecommended.recommendedGapPercent.toFixed(1)}%
                        </span>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${rvRecommended.statusBg} ${rvRecommended.statusColor}`}>
                          {rvRecommended.status}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed border-t border-gray-600/30 pt-2">
                      המחיר המומלץ מבוסס על עסקאות שבוצעו בפועל בשכונה ועל מאפייני הנכס. זהו כלי תומך החלטה ואינו מהווה הערכת שמאי.
                    </p>
                  </>
                )}
              </div>

              {/* AI Property Advisor */}
              <div className="bg-gradient-to-br from-blue-950/60 to-indigo-950/40 border border-blue-700/30 rounded-lg p-3.5">
                <div className="flex items-center gap-1.5 mb-3">
                  <Sparkles size={13} className="text-blue-400" />
                  <span className="text-xs font-bold text-blue-300">יועץ AI לנכס</span>
                </div>

                {advisorLoading && (
                  <div className="flex items-center gap-2 text-blue-400 text-xs py-3">
                    <Loader2 size={14} className="animate-spin shrink-0" />
                    <span>מנתח נכס...</span>
                  </div>
                )}

                {advisorError && !advisorLoading && (
                  <p className="text-xs text-gray-500 italic">{advisorError}</p>
                )}

                {advisor && !advisorLoading && (
                  <div className="space-y-3">
                    {/* Executive Summary */}
                    <div>
                      <p className="text-xs font-semibold text-gray-300 mb-1">סיכום מנהלים</p>
                      <p className="text-xs text-gray-300 leading-relaxed">{advisor.executive_summary}</p>
                    </div>

                    {/* Marketability */}
                    <div className={`rounded-md px-2.5 py-2 border ${
                      advisor.marketability_assessment.level.includes('גבוה')
                        ? 'bg-green-500/10 border-green-600/30'
                        : advisor.marketability_assessment.level.includes('בינוני')
                          ? 'bg-yellow-500/10 border-yellow-600/30'
                          : 'bg-red-500/10 border-red-600/30'
                    }`}>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className={`text-xs font-bold ${
                          advisor.marketability_assessment.level.includes('גבוה')
                            ? 'text-green-400'
                            : advisor.marketability_assessment.level.includes('בינוני')
                              ? 'text-yellow-400'
                              : 'text-red-400'
                        }`}>{advisor.marketability_assessment.level}</span>
                      </div>
                      <p className="text-xs text-gray-400 leading-snug">{advisor.marketability_assessment.explanation}</p>
                    </div>

                    {/* Key Selling Points */}
                    {advisor.key_selling_points?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-300 mb-1">נקודות מכירה מרכזיות</p>
                        <ul className="space-y-0.5">
                          {advisor.key_selling_points.map((pt, i) => (
                            <li key={i} className="text-xs text-green-300 leading-snug">• {pt}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Risk Factors */}
                    {advisor.risk_factors?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-300 mb-1">גורמי סיכון</p>
                        <ul className="space-y-0.5">
                          {advisor.risk_factors.map((rf, i) => (
                            <li key={i} className="text-xs text-red-300 leading-snug">• {rf}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Marketing Strategy */}
                    <div className="border-t border-blue-700/20 pt-2.5">
                      <p className="text-xs font-semibold text-gray-300 mb-1">אסטרטגיית שיווק מומלצת</p>
                      <p className="text-xs text-blue-200 leading-relaxed">{advisor.recommended_marketing_strategy}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Comparable transactions */}
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <ArrowUpDown size={13} className="text-gray-400" />
                  <span className="text-xs font-bold text-gray-400">עסקאות אחרונות בשכונה</span>
                  {comparables.length > 0 && (
                    <span className="text-xs text-gray-500">({comparables.length})</span>
                  )}
                </div>
                {comparables.length === 0 ? (
                  <p className="text-xs text-gray-500 bg-gray-700/30 rounded-lg px-3 py-2.5">לא נמצאו עסקאות אחרונות בשכונה</p>
                ) : (
                  <div className="space-y-2">
                    {comparables.map((tx, i) => (
                      <div key={i} className="bg-gray-700/40 rounded-lg px-3 py-2.5 space-y-1">
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-xs text-gray-200 leading-snug flex-1">{tx.full_address || '—'}</span>
                          <span className="text-xs text-gray-500 shrink-0 tabular-nums">
                            {tx.transaction_date ? formatDate(tx.transaction_date) : '—'}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-400">
                          {tx.size_sqm != null && <span>{tx.size_sqm} מ"ר</span>}
                          {tx.rooms != null && <span>{tx.rooms} חד'</span>}
                          {tx.floor != null && tx.floor !== '' && <span>קומה {tx.floor}</span>}
                          {tx.price != null && (
                            <span className="text-green-400 font-medium">
                              ₪{Math.round(tx.price).toLocaleString('he-IL')}
                            </span>
                          )}
                          {tx.price_per_sqm != null && (
                            <span className="text-gray-500">
                              ₪{Math.round(tx.price_per_sqm).toLocaleString('he-IL')}/מ"ר
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Missing fields notice */}
              {result.missingFields.length > 0 && (
                <div className="bg-yellow-900/20 border border-yellow-700/30 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Info size={13} className="text-yellow-400" />
                    <span className="text-xs font-bold text-yellow-400">שדות חסרים (ציון ניטרלי)</span>
                  </div>
                  <p className="text-xs text-yellow-300">{result.missingFields.join(' | ')}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {expandPanel && (
        <NeighborhoodExpandModal
          variant={expandPanel}
          raw={expandPanel === 'strengths' ? (nsRow?.strengths as string | null | undefined) : (nsRow?.weaknesses as string | null | undefined)}
          onClose={() => setExpandPanel(null)}
        />
      )}

      {showPrintReport && (
        <PropertyRatingPrintReport
          formId={formId}
          city={city}
          neighborhood={neighborhood}
          onClose={() => setShowPrintReport(false)}
          advisor={advisor}
        />
      )}
    </div>
  );
}
